import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiErrorDetail } from "../lib/api";
import { Bike, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import { useForceDarkTheme } from "../lib/theme";

/**
 * Phase 3.83 — Neues Passwort setzen (über Reset-Token aus dem Link).
 */
export default function ResetPasswordPage() {
    useForceDarkTheme();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const tokenFromUrl = params.get("token") || "";

    const [token, setToken] = useState(tokenFromUrl);
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (pw.length < 6) {
            setError("Passwort muss mindestens 6 Zeichen lang sein.");
            return;
        }
        if (pw !== pw2) {
            setError("Die beiden Passwörter stimmen nicht überein.");
            return;
        }
        if (!token || token.length < 16) {
            setError("Ungültiger Reset-Token.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pw });
            setDone(true);
            setTimeout(() => navigate("/login"), 2500);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 justify-center mb-8">
                    <Logo className="contents" boxClass="w-12 h-12" iconClass="w-7 h-7" deClass="text-black/40" />
                </Link>

                <div className="bg-white border border-gray-200 rounded-sm p-8">
                    {done ? (
                        <div className="text-center" data-testid="reset-password-success">
                            <div className="flex justify-center mb-4">
                                <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                                </div>
                            </div>
                            <h2 className="font-display text-2xl font-black tracking-tight mb-2">Passwort aktualisiert</h2>
                            <p className="text-gray-600 text-sm mb-6">Sie werden gleich zum Login weitergeleitet…</p>
                            <Link
                                to="/login"
                                className="inline-block bg-[#111827] hover:bg-black text-white font-bold rounded-sm px-6 py-2.5 text-sm uppercase tracking-wider"
                                data-testid="reset-password-to-login"
                            >
                                Zum Login
                            </Link>
                        </div>
                    ) : (
                        <>
                            <h1 className="font-display text-3xl font-black tracking-tight mb-1">Neues Passwort</h1>
                            <p className="text-gray-600 text-sm mb-6">
                                Wählen Sie ein neues, sicheres Passwort. Der Reset-Token ist 1 Stunde gültig und nur einmal verwendbar.
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {!tokenFromUrl && (
                                    <div>
                                        <Label htmlFor="token" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                                            Reset-Token
                                        </Label>
                                        <Input
                                            id="token"
                                            type="text"
                                            value={token}
                                            onChange={(e) => setToken(e.target.value.trim())}
                                            required
                                            className="mt-1.5 h-11 rounded-sm font-mono-num"
                                            data-testid="reset-password-token"
                                        />
                                    </div>
                                )}

                                <div>
                                    <Label htmlFor="pw" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                                        Neues Passwort
                                    </Label>
                                    <div className="relative mt-1.5">
                                        <Input
                                            id="pw"
                                            type={show ? "text" : "password"}
                                            value={pw}
                                            onChange={(e) => setPw(e.target.value)}
                                            required
                                            minLength={6}
                                            autoComplete="new-password"
                                            className="h-11 rounded-sm pr-11"
                                            data-testid="reset-password-new"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShow((v) => !v)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-500 hover:text-[#111827]"
                                            aria-label={show ? "Passwort verbergen" : "Passwort anzeigen"}
                                            data-testid="reset-password-toggle"
                                        >
                                            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="pw2" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                                        Passwort wiederholen
                                    </Label>
                                    <Input
                                        id="pw2"
                                        type={show ? "text" : "password"}
                                        value={pw2}
                                        onChange={(e) => setPw2(e.target.value)}
                                        required
                                        minLength={6}
                                        autoComplete="new-password"
                                        className="mt-1.5 h-11 rounded-sm"
                                        data-testid="reset-password-confirm"
                                    />
                                </div>

                                {error && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm" data-testid="reset-password-error">
                                        {error}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    disabled={loading || !pw || !pw2 || !token}
                                    className="w-full h-12 bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
                                    data-testid="reset-password-submit"
                                >
                                    {loading ? "Speichere…" : "Passwort setzen"}
                                </Button>
                            </form>

                            <div className="mt-6 pt-6 border-t border-gray-100 text-sm text-center text-gray-600">
                                <Link to="/login" className="font-bold underline decoration-2 underline-offset-4 text-[#111827]" data-testid="reset-password-login-link">
                                    Zurück zum Login
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
